import { ColorIndex } from '@/constants/ChartColorIndex';
import { QueryKeys } from '@/constants/QueryKeys';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { formatNumber, formatPercent } from '@shared/utilities';
import { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';
import { PieDataItemOption } from 'echarts/types/src/chart/pie/PieSeries';
import i18next from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

interface TierProgressChartProps {
  program: ApiRewardsProgram,
}

interface PieDataItem extends PieDataItemOption {
  labelText: string,
}

export const TierProgressChart = ({
  program,
}: TierProgressChartProps) => {
  const { t } = useTranslation();

  const { data: tierCustomerCount } = useQuery(
    [QueryKeys.GET_TIER_CUSTOMER_COUNT, program],
    () => RetailerApi.getCustomerCountsPerTier(program.retailerId, program.id),
  );

  const totalCount = Object.values(tierCustomerCount ?? {}).reduce((sum, count) => sum + count, 0);

  const noTierCount = tierCustomerCount?.['none'] ?? 0;
  const noTierPercent = formatPercent((noTierCount / totalCount) * 100, i18next);
  const seriesData: PieDataItem[] = [{
    labelText: `{bold|${noTierPercent}}`,
    name: t($ => $.overview.tierProgress.noTier),
    value: noTierCount || '-',
  }];

  program.tiers?.forEach((tier) => {
    const count = tierCustomerCount?.[tier.name] ?? 0;
    const percent = formatPercent((count / totalCount) * 100, i18next);
    seriesData.push({
      labelText: `{bold|${percent}}`,
      name: tier.name,
      value: count || '-',
    });
  });

  const options: EChartsOption = {
    color: ColorIndex,
    graphic: {
      left: 'center',
      style: {
        text: [
          `{totalCount|${formatNumber(totalCount, i18next)}}`,
          `{customerLabel|${t($ => $.common.order.customers)}}`,
        ].join('\n'),
        textAlign: 'center',
        rich: {
          customerLabel: {
            fontSize: 14,
            fontWeight: 'normal',
          },
          totalCount: {
            fontSize: 35,
            fontWeight: 'bold',
          },
        },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any, // necessary because we get error on textAlign property which actually works
      top: 'center',
      type: 'text',
    },
    legend: {
      borderRadius: 1,
      bottom: '0%',
      itemGap: 12,
      itemHeight: 10,
      itemWidth: 10,
      left: '20px',
      right: '20px',
      selectedMode: false,
      selectorLabel: {
        fontSize: 12,
      },
    },
    series: [{
      data: seriesData,
      label: {
        formatter: (params) => (params.data as PieDataItem).labelText,
        overflow: 'none',
        position: 'outer',
        rich: {
          bold: {
            fontSize: 12,
            fontWeight: 'bold',
          },
        },
      },
      labelLine: {
        length: 9,
        length2: 4,
      },
      radius: ['41%', '68%'],
      type: 'pie',
    }],
    tooltip: {
      showContent: true,
    },
  };
  return (
    <ReactECharts option={options} style={{ height: '392px', width: '100%' }} />
  );
};
